// src/admin/hooks/useAuditLog.ts
// Reads admin_audit_log with optional filter predicates, then does one
// follow-up round-trip to resolve admin_user_id into display_name/email.
//
// Schema notes that differ from the draft plan:
//   - The DB columns are `target_table` and `diff_json`; the hook exposes
//     them as `target_type` and `payload` for consistency with recordAudit.
//     We apply PostgREST `alias:column` syntax in the select so rows
//     arrive with the friendly names.
//   - database.types marks `admin_users.Relationships: []`, so we can't
//     rely on `admin_users!fkey(...)` typing. Instead of a join, we do a
//     second select on admin_users with .in('id', ids) and stitch the
//     result in client-side. For 500-row pages this is one extra RTT.
//
// Filter behaviour:
//   - Empty/undefined filter values are skipped (the URL-bound screen
//     clears a field to "" and we treat it as unset).
//   - `from` and `to` are ISO date strings ("YYYY-MM-DD"); the DB column
//     is timestamptz, and Postgres casts the date to 00:00 UTC.
//
// Ordering + cap:
//   - Always order created_at DESC (newest first).
//   - Cap at 500 rows per query to bound the client memory footprint;
//     a later pagination pass can add cursor-based infinite scroll.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';

export interface AuditRow {
  id: string;
  created_at: string;
  admin_user_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload: Record<string, unknown> | null;
  admin_user: { id: string; display_name: string | null; email: string } | null;
}

export interface AuditFilters {
  action?: string;
  admin_id?: string;
  target_type?: string;
  from?: string; // ISO date "YYYY-MM-DD"
  to?: string;   // ISO date "YYYY-MM-DD"
}

type RawAuditRow = Omit<AuditRow, 'admin_user'>;

export function useAuditLog(filters: AuditFilters = {}) {
  return useQuery<AuditRow[]>({
    queryKey: ['audit-log', filters],
    queryFn: async () => {
      let q = supabase
        .from('admin_audit_log')
        .select(
          'id, created_at, admin_user_id, action, target_type:target_table, target_id, payload:diff_json',
        );
      if (filters.action) q = q.eq('action', filters.action);
      if (filters.admin_id) q = q.eq('admin_user_id', filters.admin_id);
      if (filters.target_type) q = q.eq('target_table', filters.target_type);
      if (filters.from) q = q.gte('created_at', filters.from);
      if (filters.to) q = q.lte('created_at', filters.to);
      const { data, error } = await q
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const raws = (data ?? []) as unknown as RawAuditRow[];
      if (raws.length === 0) return [];

      // Unique non-null admin ids for the follow-up lookup.
      const adminIds = Array.from(
        new Set(raws.map((r) => r.admin_user_id).filter((x): x is string => !!x)),
      );
      let adminMap = new Map<string, { id: string; display_name: string | null; email: string }>();
      if (adminIds.length > 0) {
        const { data: admins, error: adminErr } = await supabase
          .from('admin_users')
          .select('id, display_name, email')
          .in('id', adminIds);
        if (adminErr) throw adminErr;
        adminMap = new Map(
          (admins ?? []).map((a) => [
            a.id,
            { id: a.id, display_name: a.display_name, email: a.email },
          ]),
        );
      }

      return raws.map((r): AuditRow => ({
        ...r,
        admin_user: r.admin_user_id ? adminMap.get(r.admin_user_id) ?? null : null,
      }));
    },
  });
}
